(() => {
  const formatMoney = (cents, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format((Number(cents) || 0) / 100);

  const conditionPresentation = (rawValue = '') => {
    const value = String(rawValue).trim().toLowerCase();
    const catalogueCopy = 'The product condition is shown exactly as supplied in the catalogue. Contact our team if you would like unit-specific detail before ordering.';
    if (value.includes('premium')) return { label: 'Cybist Renewed – Premium', copy: catalogueCopy };
    if (value.includes('excellent')) return { label: 'Cybist Renewed – Excellent', copy: 'Minimal signs of previous use. Fully functional, professionally prepared and backed by our one-year warranty.' };
    if (value.includes('very good')) return { label: 'Cybist Renewed – Very Good', copy: 'Light cosmetic signs consistent with careful previous use. Fully functional, professionally prepared and backed by our one-year warranty.' };
    if (value.includes('good')) return { label: 'Cybist Renewed – Good', copy: catalogueCopy };
    if (value.includes('fair')) return { label: 'Cybist Renewed – Value Grade', copy: 'Visible cosmetic marks offering exceptional value. Fully functional, professionally prepared and covered by the same one-year warranty.' };
    if (value.includes('open box') || value.includes('open-box')) return { label: 'New – Open Box', copy: 'A new product whose outer packaging has been opened or may show storage or handling wear. Product-specific details are shown in the description.' };
    if (value === 'new') return { label: 'New', copy: 'Supplied as a new product. Product-specific warranty and condition details are shown in the description.' };
    if (rawValue) return { label: String(rawValue), copy: 'The product condition is shown exactly as supplied in the catalogue. Contact our team if you would like unit-specific detail before ordering.' };
    return { label: 'Condition shown in product details', copy: 'Please review the product description or contact our team for condition details before ordering.' };
  };

  class CybistPremiumProduct {
    constructor(root) {
      if (root.dataset.cppReady === 'true') return;
      root.dataset.cppReady = 'true';
      this.root = root;
      this.currency = root.dataset.cppCurrency || window.Shopify?.currency?.active || 'GBP';
      this.variants = this.readVariants();
      this.bindGallery();
      this.bindOptions();
      this.bindForm();
      this.bindTabs();
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector('[data-cpp-variants]')?.textContent || '[]');
      } catch (error) {
        console.warn('Cybist universal premium product: variant data could not be read.', error);
        return [];
      }
    }

    bindGallery() {
      const gallery = this.root.querySelector('[data-cpp-gallery]');
      if (!gallery) return;
      this.thumbs = [...gallery.querySelectorAll('[data-cpp-thumb]')];
      this.mainImage = gallery.querySelector('[data-cpp-main-image]');
      this.galleryIndex = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.showImage(index)));
      gallery.querySelector('[data-cpp-gallery-prev]')?.addEventListener('click', () => this.showImage(this.galleryIndex - 1));
      gallery.querySelector('[data-cpp-gallery-next]')?.addEventListener('click', () => this.showImage(this.galleryIndex + 1));
      if (this.thumbs.length < 2) {
        gallery.querySelectorAll('.cpp-gallery__arrow').forEach((arrow) => { arrow.hidden = true; });
      }
    }

    showImage(index) {
      if (!this.mainImage || !this.thumbs?.length) return;
      this.galleryIndex = (index + this.thumbs.length) % this.thumbs.length;
      const thumb = this.thumbs[this.galleryIndex];
      this.mainImage.src = thumb.dataset.imageSrc;
      this.mainImage.srcset = '';
      this.mainImage.alt = thumb.dataset.imageAlt || '';
      this.thumbs.forEach((item, itemIndex) => {
        const active = itemIndex === this.galleryIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', String(active));
      });
    }

    bindOptions() {
      this.root.querySelectorAll('[data-cpp-options] input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => this.selectVariant());
      });
    }

    selectedOptions() {
      return [...this.root.querySelectorAll('.cpp-option')].map((option) => option.querySelector('input:checked')?.value);
    }

    selectedVariant() {
      const selected = this.selectedOptions();
      if (!selected.length) {
        const id = this.root.querySelector('[data-cpp-variant-id]')?.value;
        return this.variants.find((item) => String(item.id) === String(id));
      }
      return this.variants.find((item) => item.options.length === selected.length && item.options.every((value, index) => value === selected[index]));
    }

    selectVariant() {
      const variant = this.selectedVariant();
      if (!variant) {
        this.showUnavailableCombination();
        return;
      }
      this.updateVariant(variant);
    }

    bindForm() {
      this.root.querySelectorAll('.cpp-form').forEach((form) => {
        form.addEventListener('submit', (event) => {
          const variant = this.selectedVariant();
          if (!variant || !variant.available) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (!variant) this.showUnavailableCombination();
            return;
          }
          this.root.querySelectorAll('[data-cpp-variant-id]').forEach((input) => {
            input.disabled = false;
            input.value = variant.id;
          });
        }, true);
      });
    }

    showUnavailableCombination() {
      this.conditionFallback ||= [...this.root.querySelectorAll('[data-cpp-condition-label], [data-cpp-condition-copy]')]
        .map((target) => ({ target, text: target.textContent }));
      this.root.querySelectorAll('[data-cpp-variant-id]').forEach((input) => {
        input.value = '';
        input.disabled = true;
      });
      this.root.querySelectorAll('[data-cpp-sku]').forEach((target) => { target.textContent = '—'; });
      this.root.querySelectorAll('[data-cpp-price-ex], [data-cpp-price-inc]').forEach((target) => { target.textContent = '—'; });
      this.root.querySelectorAll('[data-cpp-condition-label]').forEach((target) => { target.textContent = 'Availability enquiry'; });
      this.root.querySelectorAll('[data-cpp-condition-copy]').forEach((target) => {
        target.textContent = 'Leave your details below and our team will help with availability for your selection.';
      });
      this.root.querySelector('[data-cpp-delivery]')?.classList.add('is-hidden');
      this.root.querySelector('[data-cpp-notify]')?.classList.remove('is-hidden');
      const button = this.root.querySelector('[data-cpp-add]');
      if (button) {
        button.disabled = true;
        button.textContent = 'Enquire below';
      }
      this.root.querySelectorAll('[data-cpp-notify-sku]').forEach((input) => { input.value = ''; });
      this.root.querySelectorAll('[data-cpp-notify-variant]').forEach((input) => {
        input.value = this.selectedOptions().filter(Boolean).join(' / ');
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    }

    updateVariant(variant) {
      this.root.querySelectorAll('[data-cpp-variant-id]').forEach((input) => {
        input.disabled = false;
        input.value = variant.id;
      });
      this.root.querySelectorAll('[data-cpp-sku]').forEach((sku) => {
        sku.textContent = variant.sku || variant.id;
      });
      const exPrice = this.root.querySelector('[data-cpp-price-ex]');
      const incPrice = this.root.querySelector('[data-cpp-price-inc]');
      if (exPrice) exPrice.textContent = formatMoney(Math.round(variant.price * 5 / 6), this.currency);
      if (incPrice) incPrice.textContent = formatMoney(variant.price, this.currency);

      selectedValues(this.root).forEach(({ index, value }) => {
        const target = this.root.querySelector(`[data-cpp-selection-value="${index}"]`);
        if (target) target.textContent = value;
      });

      const conditionOption = [...this.root.querySelectorAll('.cpp-option')].find((option) =>
        option.querySelector('legend')?.textContent.toLowerCase().includes('condition')
      );
      if (conditionOption) {
        const presentation = conditionPresentation(conditionOption.querySelector('input:checked')?.value);
        this.root.querySelectorAll('[data-cpp-condition-label]').forEach((target) => { target.textContent = presentation.label; });
        this.root.querySelectorAll('[data-cpp-condition-copy]').forEach((target) => { target.textContent = presentation.copy; });
      } else {
        this.conditionFallback?.forEach(({ target, text }) => { target.textContent = text; });
      }

      const delivery = this.root.querySelector('[data-cpp-delivery]');
      const notify = this.root.querySelector('[data-cpp-notify]');
      const addButton = this.root.querySelector('[data-cpp-add]');
      delivery?.classList.toggle('is-hidden', !variant.available);
      notify?.classList.toggle('is-hidden', variant.available);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to basket' : 'Sold out';
      }
      const stock = this.root.querySelector('[data-cpp-stock]');
      if (
        stock &&
        variant.available &&
        delivery?.dataset.cppDeliveryMode !== 'lead-time'
      ) {
        stock.textContent = 'In stock — ready to dispatch';
      }

      this.root.querySelectorAll('[data-cpp-notify-sku]').forEach((input) => {
        input.value = variant.sku || variant.id;
      });
      this.root.querySelectorAll('[data-cpp-notify-variant]').forEach((input) => {
        input.value = variant.title || variant.options?.join(' / ') || 'Default Title';
      });

      if (variant.featured_image?.src && this.mainImage) {
        this.mainImage.src = variant.featured_image.src;
        this.mainImage.srcset = '';
        this.mainImage.alt = variant.featured_image.alt || this.mainImage.alt;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    bindTabs() {
      const tabs = [...this.root.querySelectorAll('[data-cpp-tab]')];
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.cppTab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          tabs[(index + direction + tabs.length) % tabs.length].focus();
        });
      });
    }

    activateTab(name) {
      this.root.querySelectorAll('[data-cpp-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.cppTab === name));
      });
      this.root.querySelectorAll('[data-cpp-panel]').forEach((panel) => {
        const active = panel.dataset.cppPanel === name;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }
  }

  function selectedValues(root) {
    return [...root.querySelectorAll('.cpp-option')].map((option, index) => ({
      index,
      value: option.querySelector('input:checked')?.value || ''
    }));
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-cpp-product]').forEach((root) => new CybistPremiumProduct(root));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
